// ER-2932 (Loi Koopkracht) — conditional discounts.
//
// Haugazel tags conditional discounts in the tariff simulation: [NA] (deducted from the
// regularisation invoice after one year) and [NA-DD] (deducted only when paying by direct
// debit). hgz-proxy-service exposes them per product in `discounts[]`, each line carrying
// `conditional` and `conditionCode`, behind a feature flag.
//
// Conditional discounts must be excluded from the advertised price and shown apart as
// "Cashback". The back-end keeps returning Haugazel's total, which nets every discount, so
// the front adds the conditional part back to the total and keeps only the unconditional
// part in `subtotal.discount` (the "Promo" rows and the struck price).
//
// The corrected total is flagged `total.conditionalDiscountExcluded`: inscription-service
// (ER-2931) skips its own recalculation of the forced instalment when the flag is set, and
// a product already corrected is never corrected twice.
//
// When `discounts` is absent (feature flag off or older back-end) nothing is touched and
// the app keeps its previous behaviour.

export const CONDITION_CODE_NA = "NA";
export const CONDITION_CODE_NA_DD = "NA-DD";
export const CONDITION_CODE_DD = "DD";

// Display order of the cashback lines, whatever the Haugazel order (business rule).
const CONDITION_CODE_ORDER = [CONDITION_CODE_NA, CONDITION_CODE_NA_DD, CONDITION_CODE_DD];

// Total keys to correct, mapped to the amount of the discount lines to use.
const ANNUAL_TOTAL_KEYS = {
  priceEuro: "priceEuro",
  priceEuroEco: "priceEuro",
  priceEuroVAT: "priceEuroVAT",
  priceEuroVATEco: "priceEuroVAT"
};

const MONTHLY_TOTAL_KEYS = {
  priceEuroMonthly: "priceEuro",
  priceEuroMonthlyEco: "priceEuro",
  priceEuroMonthlyVAT: "priceEuroVAT",
  priceEuroMonthlyVATEco: "priceEuroVAT"
};

function sumDiscounts(lines) {
  return lines.reduce(
    (sum, line) => ({
      priceEuro: sum.priceEuro + (line.priceEuro || 0),
      priceEuroVAT: sum.priceEuroVAT + (line.priceEuroVAT || 0)
    }),
    { priceEuro: 0, priceEuroVAT: 0 }
  );
}

/**
 * Excludes the conditional discounts from the prices of a simulated product, in place.
 * Amounts are negative, hence the subtractions: the conditional part is added back to the
 * total and removed from the promo subtotal. No-op when the product carries no `discounts`
 * or was already corrected.
 */
export function applyConditionalDiscounts(product) {
  if (!product || !Array.isArray(product.discounts) || (product.total && product.total.conditionalDiscountExcluded)) {
    return product;
  }

  let conditional = sumDiscounts(product.discounts.filter(line => line.conditional));
  let unconditional = sumDiscounts(product.discounts.filter(line => !line.conditional));

  if (product.total) {
    Object.keys(ANNUAL_TOTAL_KEYS).forEach(key => {
      if (product.total[key] !== undefined) {
        product.total[key] -= conditional[ANNUAL_TOTAL_KEYS[key]];
      }
    });

    Object.keys(MONTHLY_TOTAL_KEYS).forEach(key => {
      if (product.total[key] !== undefined) {
        product.total[key] -= conditional[MONTHLY_TOTAL_KEYS[key]] / 12;
      }
    });

    product.total.conditionalDiscountExcluded = true;
  }

  if (product.subtotal && product.subtotal.discount) {
    if (!unconditional.priceEuro && !unconditional.priceEuroVAT) {
      product.subtotal.discount = null;
    } else {
      // The subtotal keeps its own convention: a negative amount for a quantity of -1.
      let quantity = product.subtotal.discount.quantity || -1;

      product.subtotal.discount.priceEuro = unconditional.priceEuro;
      product.subtotal.discount.priceEuroVAT = unconditional.priceEuroVAT;
      product.subtotal.discount.unitaryPriceEuro = unconditional.priceEuro / quantity;
      product.subtotal.discount.unitaryPriceEuroVAT = unconditional.priceEuroVAT / quantity;
    }
  }

  return product;
}

/**
 * Sums the conditional discounts of one product or of a list of products, grouped by
 * condition code, [NA] first then [NA-DD]. Returns an empty array when there is none.
 */
export function conditionalDiscountLines(products) {
  let lines = [];

  [].concat(products).forEach(product => {
    if (!product || !Array.isArray(product.discounts)) {
      return;
    }

    product.discounts
      .filter(discount => discount.conditional)
      .forEach(discount => {
        let line = lines.find(item => item.code === discount.conditionCode);

        if (!line) {
          line = { code: discount.conditionCode, priceEuro: 0, priceEuroVAT: 0 };
          lines.push(line);
        }

        line.priceEuro += discount.priceEuro || 0;
        line.priceEuroVAT += discount.priceEuroVAT || 0;
      });
  });

  return lines.sort((a, b) => CONDITION_CODE_ORDER.indexOf(a.code) - CONDITION_CODE_ORDER.indexOf(b.code));
}

/**
 * Tells whether a product carries at least one conditional discount.
 */
export function hasConditionalDiscount(product) {
  return conditionalDiscountLines(product).length > 0;
}



// WEBPACK FOOTER //
// ./src/helpers/conditionalDiscounts.js